/**
 * AlertsCenter — slide-over panel that lists all pending production alerts.
 *
 * Sprint E · v1 renders only "invoice_missing_wo". New alert types plug
 * in via the `ROW_COMPONENTS` map below (no shell changes).
 *
 * GP 1.0 · Sprint E · Feb 2026
 */
import React from "react";
import { useI18n } from "@/context/I18nContext";
import InvoiceMissingWORow from "@/components/alerts/InvoiceMissingWORow";

const ROW_COMPONENTS = {
  invoice_missing_wo: InvoiceMissingWORow,
};

export default function AlertsCenter({
  open,
  onClose,
  alerts,
  loading,
  onRefetch,
  onRemoveLocal,
}) {
  const { t } = useI18n();
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="alerts-center-backdrop"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-40"
      />
      {/* Slide-over panel (right-aligned) */}
      <aside
        data-testid="alerts-center-panel"
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl border-l border-black flex flex-col"
      >
        <header className="px-5 py-4 border-b border-black flex items-center justify-between">
          <div>
            <div className="label-overline text-[var(--text-secondary)]">
              GP · ALERTS
            </div>
            <h2
              data-testid="alerts-center-title"
              className="font-display text-xl font-black tracking-tighter"
            >
              {t("alert_center_title")}
            </h2>
          </div>
          <button
            data-testid="alerts-center-close"
            onClick={onClose}
            className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading && (alerts?.length ?? 0) === 0 && (
            <div
              data-testid="alerts-center-loading"
              className="px-4 py-10 text-center font-mono-tech text-xs text-[var(--text-secondary)]"
            >
              {t("alert_center_loading")}
            </div>
          )}
          {!loading && (alerts?.length ?? 0) === 0 && (
            <div
              data-testid="alerts-center-empty"
              className="px-4 py-16 text-center"
            >
              <div className="text-4xl mb-3">✓</div>
              <div className="font-display text-base font-bold">
                {t("alert_center_empty")}
              </div>
            </div>
          )}
          {(alerts || []).map((a) => {
            const Row = ROW_COMPONENTS[a.alert_type];
            if (!Row) return null;
            return (
              <Row
                key={`${a.alert_type}:${a.entity_id}`}
                alert={a}
                onResolved={onRemoveLocal}
                onRefetch={onRefetch}
              />
            );
          })}
        </div>
      </aside>
    </>
  );
}
